import { useState, useEffect } from 'react';
import { useWebsites } from '@/context/WebsitesContext';
import { getAIModelConfiguration, updateAIModelConfiguration } from '@/api/aiModelConfig';

export interface AIModelConfiguration {
  id?: string;
  website_id: string;
  model_name: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  created_at?: string;
  updated_at?: string;
}

export interface AIModelConfigHook {
  configuration: AIModelConfiguration | null;
  isLoading: boolean;
  error: string | null;
  updateConfiguration: (config: Partial<AIModelConfiguration>) => Promise<void>;
  refreshConfiguration: () => Promise<void>;
}

const DEFAULT_CONFIG: Omit<AIModelConfiguration, 'website_id'> = {
  model_name: 'gpt-4o-mini',
  temperature: 0.7,
  max_tokens: 800,
  top_p: 1.0,
  frequency_penalty: 0.0,
  presence_penalty: 0.0,
};

export const useAIModelConfig = (): AIModelConfigHook => {
  const { currentWebsite } = useWebsites();
  const [configuration, setConfiguration] = useState<AIModelConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfiguration = async () => {
    if (!currentWebsite?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await getAIModelConfiguration(currentWebsite.id);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch AI model configuration');
      }

      if (result.configuration) {
        setConfiguration(result.configuration);
      } else {
        // Set default configuration if none exists
        setConfiguration({
          ...DEFAULT_CONFIG,
          website_id: currentWebsite.id
        });
      }
    } catch (err) {
      console.error('Error fetching AI model configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch configuration');
      
      // Set default configuration on error
      setConfiguration({
        ...DEFAULT_CONFIG,
        website_id: currentWebsite.id
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateConfiguration = async (config: Partial<AIModelConfiguration>) => {
    if (!currentWebsite?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await updateAIModelConfiguration(currentWebsite.id, config);

      if (!result.success) {
        throw new Error(result.error || 'Failed to update AI model configuration');
      }

      setConfiguration(result.configuration!);
    } catch (err) {
      console.error('Error updating AI model configuration:', err);
      setError(err instanceof Error ? err.message : 'Failed to update configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshConfiguration = async () => {
    await fetchConfiguration();
  };

  useEffect(() => {
    if (currentWebsite?.id) {
      fetchConfiguration();
    }
  }, [currentWebsite?.id]);

  return {
    configuration,
    isLoading,
    error,
    updateConfiguration,
    refreshConfiguration,
  };
};
